import { AND_DELIMITER, OR_DELIMITER } from '../constants';

/*
  Input:
  ======
  {
    "Xbox One": {
      "field": "TAG",
      "key": "c3.device.model",
      "op": "eq",
      "value": "Xbox One",
      "display": "Xbox One"
    },
    "COMEDY-M": {
      "field": "TAG",
      "key": "channel",
      "op": "eq",
      "value": "COMEDY-M",
      "display": "COMEDY-M"
    }
  }

  Output: When flexible is false
  ==============================
  {
    "name": "Xbox One__AND__COMEDY-M",
    "rule": [
      [
        {
          "field": "TAG",
          "key": "c3.device.model",
          "op": "eq",
          "value": "Xbox One",
          "display": "Xbox One"
        }
      ],
      [
        {
          "field": "TAG",
          "key": "channel",
          "op": "eq",
          "value": "COMEDY-M",
          "display": "COMEDY-M"
        }
      ]
    ]
  }
*/
const ALL_TRAFFIC = 'All Traffic';

const getGroupKey = (filter: any) => {
  const fieldKey = filter.originalField ? 'originalField' : 'field';
  return filter[fieldKey] === 'TAG' ? `${filter[fieldKey]}_${filter.key}` : filter[fieldKey];
};

export const getRuleDisplay = (rule) =>
  rule.op === 'not_eq' ? `NOT ${rule.display}` : rule.display;

const filtersMapToRule = (filtersMap) => {
  const groupKeys = new Set();
  const filters = Object.values(filtersMap);
  const $groupKey = Symbol('groupKey');

  filters.forEach((filter: any) => {
    // attach a key for grouping filters
    const key = getGroupKey(filter);
    filter[$groupKey] = key; // eslint-disable-line
    groupKeys.add(key);
  });

  const rules = Array.from(groupKeys).map((key) =>
    filters.filter((f: any) => f[$groupKey] === key),
  );
  const andRules = rules.map((orRules) => orRules.map((r) => getRuleDisplay(r)).join(OR_DELIMITER));
  const ruleName = andRules.join(AND_DELIMITER);
  return {
    name: ruleName || ALL_TRAFFIC,
    rule: rules,
  };
};

/* eslint-disable no-underscore-dangle */
const getFlexibleFilterFromRules = (filtersMap) => {
  const sortedFilters = Object.values(filtersMap).sort(
    (rule1: any, rule2: any): any => rule1._order > rule2._order,
  );
  const withoutOrder = sortedFilters.filter((r: any) => r._order === undefined);
  const unorderedRules = Object.fromEntries(
    withoutOrder.map((r: any) => {
      const { key, value, display } = r;
      return [`${key}-${value}-${display}`, r];
    }),
  );

  const withOrder = sortedFilters.filter((r: any) => r._order !== undefined);
  const orders = Array.from(new Set(withOrder.map((r: any) => r._order)));
  const rules = orders.map((order) => withOrder.filter((r: any) => r._order === order));
  let name = rules
    .map((orRules) => orRules.map((rule) => getRuleDisplay(rule)).join(OR_DELIMITER))
    .join(AND_DELIMITER);

  const collatedFilter = filtersMapToRule(unorderedRules);
  rules.push(...collatedFilter.rule);
  if (collatedFilter.rule.length !== 0) {
    name = name ? `${name}${AND_DELIMITER}${collatedFilter.name}` : collatedFilter.name;
  }

  return {
    name: name || ALL_TRAFFIC,
    rule: rules,
  };
};
/* eslint-enable no-underscore-dangle */

const getFilterFromRules = (filtersMap, flexible = false) =>
  flexible ? getFlexibleFilterFromRules(filtersMap) : filtersMapToRule(filtersMap);

export default getFilterFromRules;
